<script setup lang="ts">
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useDeleteFleet } from "~/modules/fleet/composables/useDeleteFleet";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";

const props = defineProps<{
  organizationCode: number;
  fleetUid: string;
}>();
const emits = defineEmits(["deleted"]);

const { t } = useI18n();
const acl = useAcl();

const { deleteFleet, isLoading, error } = useDeleteFleet({
  organizationCode: props.organizationCode,
});

const isAllowedToManageFleet = computed(() =>
  acl.can(ACLRoles.CAN_WRITE_DEVICE),
);
const showConfirm: Ref<boolean> = ref(false);

async function onDeleteFleet() {
  await deleteFleet(props.fleetUid);

  showConfirm.value = false;

  if (LxcError.check(error.value)) {
    error.value.notify(NotificationKey.removeError);
  } else {
    showNotificationSuccess(t(NotificationKey.removeSuccess));
    emits("deleted");
  }
}

const onDeleteFleetClick = () => {
  showConfirm.value = true;
};
</script>

<template>
  <lxc-button
    html-type="button"
    type="borderless"
    :disabled="!isAllowedToManageFleet"
    :icon="!isLoading ? ILxcLightTrash2 : undefined"
    :title="
      isAllowedToManageFleet
        ? t('fleet.delete.title')
        : t('fleet.actionForbidden')
    "
    class="invisible"
    @click.stop.prevent="onDeleteFleetClick"
  >
    <lxc-loader v-if="isLoading" />
  </lxc-button>

  <confirm-modal
    :is-dialog-visible="showConfirm"
    :title="t('fleet.delete.title')"
    :description="t('fleet.delete.confirm.message')"
    :ok-label="t('button.confirm')"
    :cancel-label="t('button.cancel')"
    :icon="ILxcLightTrash2"
    icon-color-theme="error"
    color-theme="danger"
    :is-loading="isLoading"
    @confirm="onDeleteFleet"
    @cancel="showConfirm = false"
  />
</template>
